<template>
  <coozzy-card :title="$t('message.marketingSettings.referenceEmailTemplates.title')">
    <div
      v-if="loading"
      class="row">
      <div class="col text-center">
        <coozzy-spinner />
      </div>
    </div>
    <template v-else>
      <b-card-sub-title class="mb-3">
        {{ $t('message.marketingSettings.referenceEmailTemplates.employerTitle') }}
      </b-card-sub-title>
      <div class="row">
        <div
          class="col-sm-12 col-md-8"
          :class="{ 'editor-invalid': employerInvalid }">
          <editor
            ref="employer-editor"
            v-model="employerEmailTemplate"
            :api-key="editorApi"
            :disabled="readOnly || saving"
            :init="editorInit" />
        </div>
        <div class="col-sm-12 col-md-4">
          <h5>{{ $t('message.settings.availablePlaceholders') }}</h5>
          <div
            v-for="placeholder in availablePlaceholders"
            :key="'employer-' + placeholder.token"
            class="mb-2">
            <strong>{{ placeholder.token }}</strong>
            <div class="text-muted small">
              {{ placeholder.description }}
            </div>
          </div>
        </div>
      </div>
      <div class="row mb-4">
        <div class="col">
          <coozzy-button
            v-if="isCompanyAdmin"
            design="primary"
            class="float-right mt-3"
            :disabled="saving"
            @click="saveEmployerClicked">
            {{ $t('message.generic.form.save') }}
          </coozzy-button>
        </div>
      </div>

      <b-card-sub-title class="mb-3">
        {{ $t('message.marketingSettings.referenceEmailTemplates.landlordTitle') }}
      </b-card-sub-title>
      <div class="row">
        <div
          class="col-sm-12 col-md-8"
          :class="{ 'editor-invalid': landlordInvalid }">
          <editor
            ref="landlord-editor"
            v-model="landlordEmailTemplate"
            :api-key="editorApi"
            :disabled="readOnly || saving"
            :init="editorInit" />
        </div>
        <div class="col-sm-12 col-md-4">
          <h5>{{ $t('message.settings.availablePlaceholders') }}</h5>
          <div
            v-for="placeholder in availablePlaceholders"
            :key="'landlord-' + placeholder.token"
            class="mb-2">
            <strong>{{ placeholder.token }}</strong>
            <div class="text-muted small">
              {{ placeholder.description }}
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <coozzy-button
            v-if="isCompanyAdmin"
            design="primary"
            class="float-right mt-3"
            :disabled="saving"
            @click="saveLandlordClicked">
            {{ $t('message.generic.form.save') }}
          </coozzy-button>
        </div>
      </div>
    </template>
  </coozzy-card>
</template>

<script>
import CoozzyCard from '@/framework/components/card/CoozzyCard'
import CoozzyButton from '@/framework/components/button/CoozzyButton'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner'
import { user } from '@/mixins/user'
import { media } from '@/mixins/media'
import Vue from 'vue'
import Editor from '@tinymce/tinymce-vue'
import MediaApi from '@/misc/apis/MediaApi'
import {
  APPLICANT_NAME_PLACEHOLDER,
  REFERENCE_LINK_PLACEHOLDER,
  loadReferenceCheckSettings,
  preserveEmptyEmailBlocks,
  saveReferenceCheckSettings,
  templateContainsReferenceLink
} from '@/settings/marketing/referenceCheckSettings'

export default {
  name: 'ReferenceEmailTemplatesSettings',
  components: {
    CoozzySpinner,
    CoozzyButton,
    CoozzyCard,
    Editor
  },
  mixins: [user, media],
  data() {
    return {
      loading: false,
      saving: false,
      employerEmailTemplate: '',
      landlordEmailTemplate: '',
      employerInvalid: false,
      landlordInvalid: false
    }
  },
  computed: {
    readOnly() {
      return !this.isCompanyAdmin
    },
    availablePlaceholders() {
      return [
        {
          token: APPLICANT_NAME_PLACEHOLDER,
          description: this.$t('message.marketingSettings.referenceEmailTemplates.placeholders.applicantName')
        },
        {
          token: REFERENCE_LINK_PLACEHOLDER,
          description: this.$t('message.marketingSettings.referenceEmailTemplates.placeholders.referenceLink')
        }
      ]
    },
    editorApi() {
      return 'gpl'
    },
    fontSetting() {
      return this.$store.getters['user/getFontSettings']
    },
    contentStyle() {
      const fontFamily = this.getConfiguredFontFamily()
      const fontSize = this.getConfiguredFontSize()
      const fontColor = this.getConfiguredFontColor()
      return `body { color: ${fontColor} !important; font-family: ${fontFamily} !important; font-size: ${fontSize};} p {margin: 0}`
    },
    fontsizeFormats() {
      const sizeOptions = []
      for (let i = 4; i <= 36; i++) {
        sizeOptions.push(`${i}pt`)
      }
      return sizeOptions.join(' ')
    },
    fontFormats() {
      const defaultFont = 'Lato=Lato, sans-serif;'
      const userFont = this.fontSetting && this.fontSetting.family !== '' ? `${this.fontSetting.family}=${this.fontSetting.family}, sans-serif;` : defaultFont
      return `${userFont}`
    },
    editorInit() {
      return {
        toolbar: 'undo redo | bold italic underline forecolor | fontsize | numlist bullist link table | image | code | fullscreen print',
        plugins: 'fullscreen lists code image table link',
        height: '370',
        menubar: '',
        language: this.currentLanguage,
        elementpath: false,
        automatic_uploads: true,
        images_upload_handler: this.uploadImage,
        license_key: 'gpl',
        content_style: this.contentStyle,
        font_formats: this.fontFormats,
        branding: false,
        remove_trailing_brs: false,
        browser_spellcheck: true,
        table_toolbar: '',
        font_size_formats: this.fontsizeFormats,
        forced_root_block: 'div',
        newline_behavior: 'block',
        verify_html: false,
        setup: this.setupReferenceEmailEditor
      }
    }
  },
  watch: {
    employerEmailTemplate(value) {
      if (this.employerInvalid && templateContainsReferenceLink(value)) {
        this.employerInvalid = false
      }
    },
    landlordEmailTemplate(value) {
      if (this.landlordInvalid && templateContainsReferenceLink(value)) {
        this.landlordInvalid = false
      }
    }
  },
  mounted() {
    this.loadSettings()
  },
  methods: {
    getConfiguredFontFamily() {
      return this.fontSetting?.family || 'Lato, sans-serif'
    },
    getConfiguredFontSize() {
      return (this.fontSetting?.size || 10) + 'pt'
    },
    getConfiguredFontColor() {
      const color = this.fontSetting?.color
      if (typeof color === 'string' && color.trim() !== '') {
        return color
      }
      if (color && typeof color === 'object' && typeof color.red === 'number') {
        return `rgba(${color.red}, ${color.green}, ${color.blue}, ${color.alpha})`
      }
      return '#495057'
    },
    getEditorContent(refName) {
      const editorRef = this.$refs[refName]
      return editorRef?.editor?.getContent?.() || ''
    },
    /**
     * Convert plain-text paste so each blank line becomes its own empty div
     * (forced_root_block is div). Non-empty lines become content divs.
     */
    convertPlainTextToDivBlocks(text) {
      const escapeHtml = value => value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')

      return text.split(/\r?\n/).map(line => {
        if (line.trim() === '') {
          return '<div>&nbsp;</div>'
        }
        return `<div>${escapeHtml(line)}</div>`
      }).join('')
    },
    setupReferenceEmailEditor(editor) {
      editor.on('paste', (e) => {
        const clipboardData = e.clipboardData || window.clipboardData
        if (!clipboardData) {
          return
        }
        const htmlData = clipboardData.getData('text/html')
        const textData = clipboardData.getData('text/plain')
        if (htmlData && htmlData.trim() !== '') {
          return
        }
        if (textData) {
          e.preventDefault()
          editor.insertContent(this.convertPlainTextToDivBlocks(textData))
        }
      })
    },
    loadSettings() {
      this.loading = true
      loadReferenceCheckSettings(this.accountId, this.currentLanguage)
        .then(settings => {
          this.employerEmailTemplate = settings.employerEmailTemplate
          this.landlordEmailTemplate = settings.landlordEmailTemplate
        })
        .catch(() => {
          Vue.toasted.show(this.$t('message.loadingErrors.generic'), { type: 'error' })
        })
        .finally(() => {
          this.loading = false
        })
    },
    saveEmployerClicked() {
      const content = preserveEmptyEmailBlocks(
        this.getEditorContent('employer-editor') || this.employerEmailTemplate
      )
      if (!templateContainsReferenceLink(content)) {
        this.employerInvalid = true
        Vue.toasted.show(this.$t('message.marketingSettings.referenceEmailTemplates.missingReferenceLink'), { type: 'error' })
        return
      }
      this.employerInvalid = false
      this.employerEmailTemplate = content
      this.saveTemplate({ employerEmailTemplate: content })
    },
    saveLandlordClicked() {
      const content = preserveEmptyEmailBlocks(
        this.getEditorContent('landlord-editor') || this.landlordEmailTemplate
      )
      if (!templateContainsReferenceLink(content)) {
        this.landlordInvalid = true
        Vue.toasted.show(this.$t('message.marketingSettings.referenceEmailTemplates.missingReferenceLink'), { type: 'error' })
        return
      }
      this.landlordInvalid = false
      this.landlordEmailTemplate = content
      this.saveTemplate({ landlordEmailTemplate: content })
    },
    saveTemplate(patch) {
      this.saving = true
      saveReferenceCheckSettings(this.accountId, this.currentLanguage, patch)
        .then(() => {
          Vue.toasted.show(this.$t('message.marketingSettings.referenceEmailTemplates.savedSuccessfully'), { type: 'success' })
        })
        .catch(() => {
          Vue.toasted.show(this.$t('message.marketingSettings.referenceEmailTemplates.savedFailed'), { type: 'error' })
        })
        .finally(() => {
          this.saving = false
        })
    },
    async uploadImage(blobInfo, success, failure) {
      if (blobInfo) {
        const formData = new FormData()
        const fileName = this.editFileName(blobInfo.filename())
        formData.append('file', blobInfo.blob(), fileName)
        formData.append('filename', blobInfo.filename())
        formData.append('public', 'true')
        try {
          const response = await MediaApi.uploadMedia(formData)
          return response.url
        } catch (e) {
          if (e.response && e.response.data && e.response.data.message === 'Not supported MIME type') {
            Vue.toasted.show(this.$t('message.savingErrors.fileNotSupported'), { type: 'error' })
          } else {
            Vue.toasted.show(this.$t('message.savingErrors.file'), { type: 'error' })
          }
          if (failure) {
            failure(e)
          }
          return e
        }
      }
      if (success) {
        success('')
      }
      return ''
    }
  }
}
</script>

<style lang="scss" scoped>
.editor-invalid {
  :deep(.tox-tinymce) {
    border: 1px solid #dc3545 !important;
  }
}
</style>
